import { useState } from 'react'
import { Pie } from '@visx/shape'

import { Legend } from '../common/legend/legend'
import { LegendKey, LEGEND_SHAPE } from '../common/legend/legend-key'
import { TooltipContextProvider, useTooltipDisplayContext } from '../common/tooltip/tooltip.context'
import { Tooltip } from '../common/tooltip/tooltip'
import { formatService } from '../common/format.service'

import { sunburstService, EMPTY_SLICE_TYPE } from './sunburst.service'

import { useStyles } from './sunburst.style'

const DEFAULT_WIDTH = 1024
const INIT_RADIUS = 120
const SLICE_THICKNESS = 30
const BORDER_WIDTH = 1

export const SunburstContainer = function (props = {}) {
  const {
    children,
    data,
    types
  } = props

  const typeColors = props.colors || {}

  if (!data || !types) {
    return null
  }

  const colors = Object.assign(sunburstService.getTypeColors(types), typeColors)

  return children({ colors })
}

export const SingleSunburst = function ({
  data,
  types,
  valueField = 'value',
  colors = {},
  totalLabel = 'Total Patients',
  width = DEFAULT_WIDTH,
  isCompressed = false
}) {
  const classes = useStyles()

  const [pathLabels, setPathLabels] = useState([])

  function onTooltipShow (sunburstPath) {
    setPathLabels(sunburstPath)
  }

  function onTooltipHide () {
    setPathLabels([])
  }

  const total = data.reduce((sum, slice) => (sum + slice.value), 0)

  return (
    <TooltipContextProvider>
      <div className={`${classes.sunburstContainer} ${isCompressed ? classes.compressedStyles : ''}`}>
        <SunburstWithTooltip data={data} colors={colors} total={total} width={width} valueField={valueField} onTooltipShow={onTooltipShow} onTooltipHide={onTooltipHide} />
        <div className={classes.total}>
          <div className={classes.totalLabel}>{totalLabel}</div>
          <div className={classes.totalValue}>{formatService.formatNumber(total)}</div>
        </div>
        {pathLabels.length > 0 &&
          <div className={classes.pathContainer}>
            <div className={classes.path}>
              <div className={classes.pathLabel}>Sequence</div>
              {pathLabels.map(({ type, color }, index) => (
                <div key={index} className={classes.legendKeyContainer}>
                  <div className={classes.legendKey}>
                    <LegendKey label={types[type]} color={color} shape={LEGEND_SHAPE.CIRCLE} />
                  </div>
                  {(index < pathLabels.length - 1) &&
                    <div className={classes.pathArrow}>↓</div>
                  }
                </div>
              ))}
            </div>
          </div>
        }
      </div>
    </TooltipContextProvider>
  )
}

export const Sunburst = function (props = {}) {
  if (!props.data || !props.types) {
    return null
  }

  const sunburstProps = { ...props }
  delete sunburstProps.colors

  return (
    <SunburstContainer {...props}>
      {({ colors }) => (
        <>
          <Legend labels={props.types ? Object.values(props.types) : []} colors={colors ? Object.values(colors) : []} position={props.legendPosition || 'right'} />
          <SingleSunburst {...sunburstProps} colors={colors} />
        </>
      )}
    </SunburstContainer>
  )
}

function SunburstWithTooltip ({ data, colors, total, width, valueField, onTooltipShow = function () {}, onTooltipHide = function () {} }) {
  const classes = useStyles()
  const [, setTooltipDisplayData] = useTooltipDisplayContext()

  function handleMouseEnter ({ data }) {
    sunburstService.selectSunburstPath(data)

    setTooltipDisplayData({
      primaryValue: formatService.formatPercent(data[valueField] / total),
      secondaryValue: formatService.formatNumber(data[valueField])
    })

    const sunburstPath = sunburstService.getSunburstPath(data)
    onTooltipShow(sunburstPath)
  }

  function handleMouseLeave () {
    sunburstService.clearSelection(data)
    setTooltipDisplayData()
    onTooltipHide()
  }

  const depth = sunburstService.getSunburstDepth(data)
  sunburstService.formatDataForDisplay(data, colors, depth)
  const pieLayers = []
  for (let index = 1; index <= depth; index++) {
    pieLayers.push(sunburstService.getSunburstDataByLayer(data, index))
  }

  // Make the height's factors (init radius, slice thickness, border width) be proportional to the width
  const initRadius = width / (DEFAULT_WIDTH / INIT_RADIUS)
  const sliceThickness = width / (DEFAULT_WIDTH / SLICE_THICKNESS)
  const borderWidth = width / (DEFAULT_WIDTH / BORDER_WIDTH)

  const height = (2 * initRadius) + (pieLayers.length - 1) * (2 * (sliceThickness + borderWidth))

  // Make the svg width the same as the calculated height, because sunburst is a perfect circle
  width = height

  return (
    <>
      <svg width={width} height={height} className={classes.svg}>
        <g transform={`translate(${width / 2}, ${height / 2})`}>
          {pieLayers.map((layer, index) => {
            const outerRadius = initRadius + (index * (sliceThickness + borderWidth))
            return (
              <Pie key={index} data={layer} pieValue={(data) => (data[valueField])} outerRadius={outerRadius} innerRadius={outerRadius - sliceThickness}
                pieSort={null} pieSortValues={null}>
                {({ arcs, path }) => (
                  arcs.map((arc, index) => {
                    if (arc.data.type === EMPTY_SLICE_TYPE) {
                      return (
                        <g key={index}>
                          <path d={path(arc)} className={classes.emptyArc} />
                        </g>
                      )
                    } else {
                      return (
                        <g key={index} onMouseEnter={() => { handleMouseEnter(arc) }} onMouseLeave={handleMouseLeave}>
                          <path d={path(arc)} fill={arc.data.color} className={`${arc.data.selected ? classes.selectedArc : classes.arc}`} />
                        </g>
                      )
                    }
                  })
                )}
              </Pie>
            )
          })}
        </g>
      </svg>
      <Tooltip defaults={{ primaryLabel: 'Proportion', secondaryLabel: 'Count' }} />
    </>
  )
}
